import React, { useState } from 'react';
import { Alert, Button } from '@billda/design-system';
import { getErrorMessage } from '@billda/shared-ui';
import { LabourGroupSelect } from '../../labour/components/LabourGroupSelect';
import {
  useAssignLabourGroup,
  useRemoveLabourGroup,
} from '../hooks/useLabourGroupAssignment';
import { resolveLabourGroupSaveIntent } from '../utils/labourGroupSaveIntent';

interface UserLabourGroupAssignmentProps {
  userId: string;
  currentGroupId?: number | null;
  currentGroupName?: string | null;
  onUpdate?: () => void;
  isEditing: boolean;
  onEditingChange: (editing: boolean) => void;
  onEditingComplete?: () => void;
}

export const UserLabourGroupAssignment: React.FC<
  UserLabourGroupAssignmentProps
> = ({
  userId,
  currentGroupId,
  currentGroupName,
  onUpdate,
  isEditing,
  onEditingChange,
  onEditingComplete,
}) => {
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(
    currentGroupId ?? null
  );

  const assignMutation = useAssignLabourGroup();

  const removeMutation = useRemoveLabourGroup();

  const isLoading = assignMutation.isPending || removeMutation.isPending;
  const error = assignMutation.error ?? removeMutation.error;
  const intent = resolveLabourGroupSaveIntent(selectedGroupId, currentGroupId);

  const resetMutations = () => {
    assignMutation.reset();
    removeMutation.reset();
  };

  const handleCancel = () => {
    setSelectedGroupId(currentGroupId ?? null);
    resetMutations();
    onEditingChange(false);
    onEditingComplete?.();
  };

  const handleSave = () => {
    if (intent.action === 'none') {
      onEditingChange(false);
      onEditingComplete?.();
      return;
    }

    const onSuccess = () => {
      onEditingChange(false);
      onUpdate?.();
      onEditingComplete?.();
    };

    if (intent.action === 'remove') {
      removeMutation.mutate(userId, { onSuccess });
      return;
    }

    assignMutation.mutate({ userId, groupId: intent.groupId }, { onSuccess });
  };

  if (isEditing) {
    return (
      <div className="mb-2 mt-0.5" data-testid="labour-group-edit-mode">
        <div className="flex flex-col gap-2">
          {error && <Alert severity="error">{getErrorMessage(error)}</Alert>}

          <LabourGroupSelect
            value={selectedGroupId}
            onChange={setSelectedGroupId}
            disabled={isLoading}
            fullWidth
          />

          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="neutral"
              size="sm"
              onClick={handleSave}
              disabled={isLoading || intent.action === 'none'}
              data-testid="labour-group-save-button"
            >
              {isLoading ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-2 mt-0.5" data-testid="labour-group-display">
      <span
        className="type-body-lg font-medium"
        data-testid="labour-group-name"
      >
        {currentGroupName || 'Not assigned'}
      </span>
    </div>
  );
};
